import { Css, Palette, Tooltip, useTestIds } from "@homebound/beam";
import { BarDatum, BarSvgProps, BarTooltipProps, ResponsiveBar } from "@nivo/bar";
import { Fragment, useMemo } from "react";

type BarChartProps = {
  dataset: BarDatum[];
  keys: string[];
  colors?: Palette[];
  disabled?: boolean;
  emptyStateTooltipText?: string;
  tooltipValueFormat?: (data: BarTooltipProps<BarDatum>) => string;
} & Omit<BarSvgProps<BarDatum>, "data" | "colors" | "isInteractive" | "height" | "width">;

export function BarChart(props: BarChartProps) {
  const { dataset, colors, disabled, tooltipValueFormat, keys, emptyStateTooltipText, ...otherProps } = props;
  const tid = useTestIds({});
  const Chart = useMemo(
    () => (
      <ResponsiveBar
        data={dataset}
        keys={keys}
        colors={colors}
        isInteractive={!disabled}
        tooltip={tooltipValueFormat ? customTooltip(tooltipValueFormat) : undefined}
        enableGridX={false}
        theme={{ axis: { ticks: { text: { fontSize: 10, fill: Palette.Gray700 } } } }}
        {...otherProps}
      />
    ),
    [dataset, colors, disabled, tooltipValueFormat, keys, otherProps],
  );

  return emptyStateTooltipText ? (
    <Tooltip {...tid.dataTooltip} title={emptyStateTooltipText} placement="top" children={Chart} />
  ) : (
    <Fragment children={Chart} />
  );
}

function customTooltip(tooltipValueFormat: (data: BarTooltipProps<BarDatum>) => string) {
  return (data: BarTooltipProps<BarDatum>) => (
    <div css={Css.bgGray900.white.xs.df.mt1.p1.br4.bshBasic.maxwPx(300).tal.$}>{tooltipValueFormat(data)}</div>
  );
}
